import { OnlyOfficeEditor } from "@/components/common/OnlyOfficeEditor.tsx";
import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

import { Tiptap } from "@/components/common/tiptap/Tiptap";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Checkbox } from "@/components/core/forms/checkbox";
import { CheckboxGroup } from "@/components/core/forms/checkbox-group";
import ComboboxCreatable from "@/components/core/forms/combox-creatable";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal";
import { ScrollArea } from "@/components/core/scroller";
import Tooltip from "@/components/core/tooltip";
import { PdfViewer } from "@/components/pdf-viewer";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { IconEye, IconFile, IconTemplate, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import moment from "moment";
import formTemplateService from "../api/services/form-templates-service";
import { type CaseActivity, type CaseActivityForm, caseActivityFormSchema } from "../types/activities";
import type { FormTemplate } from "../types/form-template";

export default function CaseActivityExpungeModal({
	caseRecord,
	data,
	title,
	handleSubmit,
}: {
	caseRecord: CaseRecord;
	data: CaseActivity;
	title: string;
	handleSubmit: (values: any) => void;
}) {
	const { closeModal } = useModalControl();
	const [activity, setActivity] = useState<FormTemplate | null>();
	const [isDisabled, setIsDisabeld] = useState(false);
	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStagesNew = caseStageQuery.data;
	const [document, setDocument] = useState<string | null>(null);
	const [selectType, setSelectType] = useState(false);
	const [type, setType] = useState(null);
	const [types, setTypes] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const showPreview = true;
	const showEditor = false;
	const [hearings, setHearings] = useState<HearingRow[]>([
		{
			title: "Hearing",
			type: "Hearing",
			assigned_to: "Both",
			remarks: "",
			due_date: "",
			stage: "",
		},
	]);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const documentTokenMutation = useDocumentConfigMutation();

	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [moveBack, setMoveBack] = useState(false);
	const [showAttachment, setShowAttachment] = useState(false);

	const localDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));

	const yyyy = localDate.getFullYear();
	const mm = String(localDate.getMonth() + 1).padStart(2, "0");
	const dd = String(localDate.getDate()).padStart(2, "0");
	const hh = String(localDate.getHours()).padStart(2, "0");
	const min = String(localDate.getMinutes()).padStart(2, "0");
	const ss = String(localDate.getSeconds()).padStart(2, "0");

	const form = useForm<CaseActivityForm>({
		resolver: zodResolver(caseActivityFormSchema),
		defaultValues: {
			id: data.id,
			status: title,
			case_id: data.case_id, //TODO update this
			stage: title,
			move_back: data.stage,
			activity_id: data.id,
			filed_at: `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`,
			assigned_to: "Both",
			format_type: "upload_files",
			party_type: "Bcc",
			activity: "",
			remarks: null,
			attachment: null,
		},
	});
	const [docOpts, setDocOpts] = useState({
		token: "",
		options: {},
	});
	const modal = useModal();
	const { watch } = form;
	const formatType = watch("format_type");

	const handleRemoveAttachment = (index: number) => {
		setSelectedFiles((prevFiles) => {
			const updatedFiles = prevFiles.filter((_, i) => i !== index);
			form.setValue("attachment", updatedFiles); // Properly updating form state
			setMultipleFiles(updatedFiles);
			setShowAttachment(updatedFiles.length > 0);
			return updatedFiles; // Ensure state is properly updated
		});
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const objectUrl = URL.createObjectURL(files[0]);
			setDocument(objectUrl);
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles); // Updating form value with new file list
				setMultipleFiles(newFiles);
				setShowAttachment(true);
				return newFiles;
			});
		}
	};

	// useEffect(() => {
	// 	const fetchOptions = async () => {
	// 		// url: "https://sc-admin.revlv.com/blank.docx",
	// 		const response = await documentTokenMutation.mutateAsync({
	// 			file_type: "docx",
	// 			title: "New Document",
	// 			url: "https://sc-admin.revlv.com/blank.docx",
	// 			mode: "edit",
	// 		});

	// 		form.setValue("document_key", response.key);
	// 		setDocOpts({
	// 			token: response.token,
	// 			options: response.options,
	// 		});
	// 	};

	// 	fetchOptions();
	// }, []);

	const storedTime = sessionStorage.getItem("password_entered_at");
	const handleSubmission = async (values: CaseActivityForm) => {
		setIsSubmitting(true);
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await handleSubmit(values);
			setIsSubmitting(false);
		} else {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "sm",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							await handleSubmit(values);
							setIsSubmitting(false);
						}}
					/>
				),
			});
		}
	};

	useEffect(() => {
		if (formatType === "text_editor") {
			documentTokenMutation
				.mutateAsync({
					file_type: "docx",
					title: `New Document for Case No.${caseRecord.case_number}`,
					mode: "edit",
				})
				.then((response) => {
					setDocOpts({
						token: response.token,
						options: response.options,
					});

					form.setValue("document_key", response.key);
				});
		} else {
			// we have to reset the document key since we don't want the backend to assume
			// we're still trying to upload a document from the text-editor
			form.setValue("document_key", null);
		}
	}, [formatType]);

	return (
		<div className="flex flex-col w-full h-full">
			<Form {...form}>
				<form className="w-full h-full" onSubmit={form.handleSubmit(handleSubmission)}>
					<div className="flex flex-col w-full h-full">
						<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
							<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
								<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
									<span className="text-base font-bold text-foreground-dark">{title}</span>
									<button
										onClick={() => {
											closeModal();
										}}
										className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
										type="button"
									>
										<IconX size={20} stroke={1.5} />
									</button>
								</div>
								<ScrollArea className="h-[calc(100svh - 120px)]">
									<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2 overflow-y-auto">
										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="format_type"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Select Format Type</FormLabel>
														</div>
														<FormGroup>
															<Select
																onValueChange={async (value) => {
																	field.onChange(value);
																	setTypes(value);
																}}
																defaultValue={field.value}
																options={[
																	{
																		value: "upload_files",
																		label: "Upload Files",
																	},
																]}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										{formatType === "text_editor" && (
											<div className="col-span-12 w-full">
												<FormField
													control={form.control}
													name="type"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Select Type</FormLabel>
															</div>
															<FormGroup>
																<Select
																	onValueChange={async (value) => {
																		field.onChange(value);
																		setType(value);
																		setSelectType(true);
																		if (value === "Custom") {
																			const response = await documentTokenMutation.mutateAsync({
																				file_type: "docx",
																				title: "New Document",
																				url: "https://sc-admin.revlv.com/blank.docx",
																				mode: "edit",
																			});
																			form.setValue("document_key", response.key);
																			setDocOpts({
																				token: response.token,
																				options: response.options,
																			});
																		}
																	}}
																	defaultValue={field.value}
																	options={[
																		{
																			value: "Custom",
																			label: "Custom",
																		},
																		{
																			value: "Template",
																			label: "Template",
																		},
																	]}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
										{formatType === "text_editor" && type === "Template" && (
											<div className="col-span-12 w-full">
												<FormField
													control={form.control}
													name="form_template_id"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Court Actions</FormLabel>
															</div>
															<FormGroup>
																<ComboboxCreatable
																	defaultValue={field.form_template_id}
																	onValueChange={async (value) => {
																		const response = await documentTokenMutation.mutateAsync({
																			file_type: "docx",
																			title: value.medias.name,
																			url: value.medias.original_url,
																			mode: "edit",
																		});

																		form.setValue("form_template_id", value.value);

																		// form.setValue("activity", value.label);
																		form.setValue("document_key", response.key);
																		setDocOpts({
																			token: response.token,
																			options: response.options,
																		});
																	}}
																	loadOptions={async (value: string) => {
																		const items = await formTemplateService.list({
																			"filter[search]": value || data.activity,
																		});

																		return items.data.map((item) => ({
																			label: item.title,
																			value: item.id,
																			...item,
																		}));
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}

										{formatType === "upload_files" && (
											<div className="col-span-12 flex w-full">
												<FormField
													control={form.control}
													name="attachment"
													render={() => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Upload Files</FormLabel>
															</div>
															<FormGroup>
																<div className="flex flex-col w-full rounded-md border border-border">
																	<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																		<input
																			accept="application/pdf"
																			type="file"
																			ref={inputRef}
																			className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																			multiple
																			onChange={handleFileChange}
																		/>
																		<div className="flex flex-row items-center w-full gap-2">
																			<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																				<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
																			</div>
																			<span className="text-base text-foreground">
																				{multipleFiles.length === 1
																					? multipleFiles[0].name
																					: multipleFiles.length > 1
																						? "More than 2 files selected"
																						: "No file selected"}
																			</span>
																		</div>
																	</div>
																	{showAttachment && multipleFiles.length > 0 && (
																		<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																			{multipleFiles.map((file, index) => (
																				<div key={index} className="flex flex-row justify-between items-center">
																					<div className="items-center flex flex-row gap-2">
																						<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																							<IconFile size={18} stroke={2} />
																						</div>
																						<div className="inline-flex flex-col">
																							<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																							<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																						</div>
																					</div>

																					<div className="inlineflex flex-row w-auto">
																						<Tooltip className="!z-[999]" content="Preview">
																							<button
																								className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-foreground/50 hover:text-foreground"
																								type="button"
																								color="red"
																								onClick={() => {
																									const objectUrl = URL.createObjectURL(file);
																									setDocument(objectUrl);
																								}}
																							>
																								<IconEye size={18} stroke={2} />
																							</button>
																						</Tooltip>
																						<Tooltip className="!z-[999]" content="Remove">
																							<button
																								className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																								type="button"
																								color="red"
																								onClick={() => handleRemoveAttachment(index)}
																							>
																								<IconTrash size={18} stroke={2} />
																							</button>
																						</Tooltip>
																					</div>
																				</div>
																			))}
																		</div>
																	)}
																</div>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="activity"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Title</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Input autoComplete="off" placeholder="Title" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="ordered_at"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Date Ordered</FormLabel>
														</div>
														<FormGroup>
															<Input autoComplete="off" type="datetime-local" placeholder="Date Ordered" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 w-full ">
											<FormField
												control={form.control}
												name="remarks"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Remarks</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Textarea {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex flex-col w-full">
											<FormField
												control={form.control}
												name="is_move_back"
												render={() => (
													<FormItem>
														<CheckboxGroup>
															<FormItem>
																<FormGroup>
																	<div className="flex flex-row w-full gap-2">
																		<FormControl>
																			<Checkbox
																				size="lg"
																				name="is_move_back"
																				checked={moveBack}
																				onCheckedChange={(value) => {
																					setMoveBack(value);
																				}}
																			/>
																		</FormControl>
																		<FormLabel>
																			<div className="flex flex-col w-full">
																				<span className="text-base text-foreground">Move Previous Stage?</span>
																			</div>
																		</FormLabel>
																	</div>
																</FormGroup>
																<FormMessage />
															</FormItem>
														</CheckboxGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										{moveBack && (
											<div className="col-span-12 w-full">
												<FormField
													control={form.control}
													name="move_back"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Move back to Stage</FormLabel>
															</div>
															<FormGroup>
																<Select
																	onValueChange={async (value) => {
																		field.onChange(value);
																	}}
																	defaultValue={field.value}
																	options={caseStagesNew.data
																		.filter((c) => c.is_visible)
																		.map((c) => ({
																			label: c.label,
																			value: c.name,
																		}))}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
									</div>
								</ScrollArea>

								<div className="flex shrink-0 flex-row items-center w-full h-[70px] px-4 gap-2 mt-auto border-t border-slate-200">
									<Button
										className="w-full"
										variant="ghost"
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										Cancel
									</Button>
									<Button className="w-full" disabled={isSubmitting} loading={isSubmitting}>
										Submit
									</Button>
								</div>
							</Panel>

							<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
								<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
							</PanelResizeHandle>

							<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
								{formatType === "text_editor" && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

								{formatType === "upload_files" && <PdfViewer file={document} />}

								{!formatType && (
									<Blanket className="my-auto self-center" width="sm">
										<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
										<BlanketInfo title="No Selected Template" message="Select Template on Select Type to load the default template for this form." />
									</Blanket>
								)}
							</Panel>
						</PanelGroup>
					</div>
				</form>
			</Form>
		</div>
	);
}
